import { cn } from '@/lib/utils';
import { LIGHT_TOKENS } from './light-tokens';
import { PAPER_INK } from '../ui/paper-tokens';

export type SerifQuoteProps = {
  /** Opening clause — ink primary (§1.2 "two-tone: opening clause `#15171C`"). */
  leadClause: string;
  /** Continuation clause — ink secondary ([AMENDED per C1]: `#6B707A`, not
   * the original draft's `#8A8F98`, which fails AA on paper). */
  restClause: string;
  /**
   * CRITICAL-1 fix (r4-p3-review-plan.md): OPTIONAL, not required. When
   * omitted, this renders as an UNATTRIBUTED editorial statement — no
   * literal quotation-mark glyphs, no `<figcaption>` byline. Only pass this
   * when a REAL, Albert-approved personal/company testimonial exists for
   * the exact words in `leadClause`/`restClause` — attaching a name to
   * CMG's own promise copy is the exact fabricated-testimonial failure Law
   * #8 (TRUTH BARRIER) bans, even when the name itself is real.
   */
  attributionName?: string;
  /** Role/company/date line — readable CONTENT, so [AMENDED per C1] routes
   * it through `ink.secondary` (4.56:1), never the decorative-only
   * `ink.eyebrowDecorative` (`#9CA3B0`, 2.33:1, fails AA). Ignored when
   * `attributionName` is omitted. */
  attributionDetail?: string;
  /**
   * 2026-07-17 R7 integration seam (design contract L3.1 / brief §2f,
   * "SerifQuoteSection's use of these primitives gets the same treatment"):
   * additive, default `'light'` (byte-identical to every existing consumer,
   * incl. the dev gallery). `surface="paper"` re-inks the two-tone clauses
   * + attribution to `PAPER_INK` — `LightBand`'s own doc comment has the
   * full cross-lane seam rationale.
   */
  surface?: 'light' | 'paper';
  className?: string;
};

/**
 * SerifQuote — the editorial pull-quote primitive (masterplan §1.2/§2.6,
 * attio ref `chrome_oywJ3ivdyg`). Two-tone: `leadClause` in ink primary,
 * `restClause` in ink secondary — "the signature attio move" per §1.2. Uses
 * the LOCKED 0-byte system-serif stack (`--font-landing-serif`, declared in
 * globals.css) at `font-weight:400` + `font-synthesis:none`
 * ([AMENDED per C4] — system Palatino/Book Antiqua expose regular/bold only;
 * a requested 500 gets browser-synthesized inconsistently).
 *
 * Plain-string props, not `BilingualContent` — matches every other `ui/**`
 * primitive's convention (locale resolution happens in the calling section
 * component, not inside the primitive), which is also what keeps this an
 * RSC (no `useLocale()` client hook here).
 *
 * Two rendering modes ([CRITICAL-1 fix], r4-p3-review-plan.md): with
 * `attributionName` this is a customer pull-quote (literal `"`/`"` glyphs +
 * `<figcaption>` byline — only for a REAL, approved testimonial); without it,
 * this is an unattributed editorial statement (no quotation marks, no
 * figcaption) — the shape the light-band `light.quote` copy actually needs,
 * since it is CMG's own company-promise copy, not something a customer said.
 */
export function SerifQuote({
  leadClause,
  restClause,
  attributionName,
  attributionDetail,
  surface = 'light',
  className,
}: SerifQuoteProps) {
  const paper = surface === 'paper';
  // PAPER_INK has no `tertiary` role (`primary`/`secondary`/`muted`/
  // `onMast*` — see `ui/paper-tokens.ts`'s `PaperInkRole` union); `muted`
  // is its closest tier to `LIGHT_TOKENS.ink.tertiary` (attribution-name
  // weight), so the paper branch maps onto that role for the one call site
  // below that uses `tertiary`.
  const inkPrimary = paper ? PAPER_INK.primary.text : LIGHT_TOKENS.ink.primary.text;
  const inkSecondary = paper ? PAPER_INK.secondary.text : LIGHT_TOKENS.ink.secondary.text;
  const inkTertiary = paper ? PAPER_INK.muted.text : LIGHT_TOKENS.ink.tertiary.text;
  return (
    <figure
      data-primitive="SerifQuote"
      data-surface={surface}
      className={cn(
        'mx-auto flex max-w-[900px] flex-col items-center gap-6 text-center',
        className
      )}
    >
      <blockquote
        // R5-P3 review fix (MINOR finding, post-review fix round): dropped
        // the grandfathered `tracking-[-0.01em]` override — design amendment
        // §1's site-wide serif law is "tracking normal (never the sans
        // tight-tracking treatment)" and explicitly lists SerifQuote among
        // the covered nodes; omitting any override here leaves the browser
        // default `normal`, matching every other converted serif node
        // (HeroSection H1, section H2s) byte-for-byte on this property. The
        // family/weight/font-synthesis mechanism (inline style, not the
        // shared `.landing-serif-display` class) is UNCHANGED — switching
        // mechanisms would also require rewriting this file's own
        // `[AMENDED per C4]` inline-style test, a larger change than the law
        // actually requires.
        //
        // R7 integration (2026-07-17, design contract "type-led hero" +
        // brief §2e): bumped clamp(30px,4.2vw,54px) -> clamp(34px,5vw,64px)
        // -- the quote becomes the page's largest type moment (t3 voice),
        // now that the journey animation demoted to a framed exhibit no
        // longer competes for that role. Only production consumer is
        // `SerifQuoteSection.tsx` (grep-verified); the dev gallery's
        // `SerifSpecimens.tsx` hand-replicates its own copy of this recipe
        // rather than importing this class, so it is unaffected.
        className="text-[clamp(34px,5vw,64px)] leading-[1.18]"
        style={{ fontFamily: LIGHT_TOKENS.serifFontVar, fontWeight: 400, fontSynthesis: 'none' }}
      >
        {attributionName ? (
          <span aria-hidden="true" className={inkPrimary}>
            &ldquo;
          </span>
        ) : null}
        <span className={inkPrimary}>{leadClause}</span>{' '}
        <span className={inkSecondary}>{restClause}</span>
        {attributionName ? (
          <span aria-hidden="true" className={inkSecondary}>
            &rdquo;
          </span>
        ) : null}
      </blockquote>
      {attributionName ? (
        <figcaption className="flex flex-col items-center gap-0.5">
          <span className={cn('text-[13px] font-medium', inkTertiary)}>{attributionName}</span>
          {attributionDetail ? (
            <span className={cn('text-[12px] font-medium', inkSecondary)}>{attributionDetail}</span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}
